import type {
  Activators,
  KeyboardCoordinateGetter,
  SensorInstance,
  SensorProps,
} from '@dnd-kit/core';
import type { Coordinates } from '@dnd-kit/utilities';
import { InputResources, type TabDragScope } from './TabDragScope';

type Options = { scope: TabDragScope; coordinateGetter: KeyboardCoordinateGetter };

export class TabKeyboardSensor implements SensorInstance {
  autoScrollEnabled = false;
  static activators: Activators<Options> = [
    {
      eventName: 'onKeyDown',
      handler: (event, { scope }, { active }) => {
        if (!scope.active || !['Space', 'Enter'].includes(event.nativeEvent.code))
          return false;
        const activator = active.activatorNode.current;
        if (activator && event.target !== activator) return false;
        event.preventDefault();
        return true;
      },
    },
  ];

  private resources: InputResources;
  private closed = false;
  private reference: Coordinates | null = null;

  constructor(private props: SensorProps<Options>) {
    this.resources = new InputResources(props.options.scope);
    this.resources.add(() => {
      this.closed = true;
    });
    if (this.closed) return;
    const node = props.activeNode.node.current;
    if (!node) {
      this.resources.dispose();
      return;
    }
    const doc = node.ownerDocument;
    // 同步注册并排除启动事件本身，杜绝卸载后setTimeout注册监听复活。
    this.resources.listen(doc, 'keydown', (event) => this.keyDown(event));
    this.resources.listen(doc, 'focusin', (event) => {
      if (event.target instanceof Node && !node.contains(event.target))
        this.finish('cancel');
    });
    this.resources.listen(doc, 'visibilitychange', () => this.finish('cancel'));
    if (doc.defaultView)
      this.resources.listen(doc.defaultView, 'resize', () => this.finish('cancel'));
    props.onStart({ x: 0, y: 0 });
  }

  private keyDown(event: KeyboardEvent) {
    if (event === this.props.event || event.defaultPrevented) return;
    const node = this.props.activeNode.node.current;
    if (!(event.target instanceof Node) || !node?.contains(event.target)) return;
    if (['Space', 'Enter', 'Tab', 'Escape'].includes(event.code)) {
      event.preventDefault();
      this.finish(event.code === 'Escape' ? 'cancel' : 'end');
      return;
    }
    const { collisionRect } = this.props.context.current;
    const current = collisionRect
      ? { x: collisionRect.left, y: collisionRect.top }
      : { x: 0, y: 0 };
    this.reference ??= current;
    const next = this.props.options.coordinateGetter(event, {
      active: this.props.active,
      context: this.props.context.current,
      currentCoordinates: current,
    });
    if (!next || this.closed) return;
    event.preventDefault();
    // 标签仅水平排序，保留内置键盘输入的“先滚动、到边界再补位移”行为。
    let scrollCorrection = 0;
    if (event.code === 'ArrowLeft' || event.code === 'ArrowRight') {
      const right = event.code === 'ArrowRight';
      for (const container of this.props.context.current.scrollableAncestors) {
        const maximum = container.scrollWidth - container.clientWidth;
        const before = container.scrollLeft;
        if (right ? before >= maximum : before <= 0) continue;
        const rect = container.getBoundingClientRect();
        const viewport = container === container.ownerDocument.scrollingElement;
        const left = viewport ? 0 : rect.left;
        const width = viewport ? container.clientWidth : rect.width;
        const clamped = Math.min(
          left + width * (right ? 0.5 : 1),
          Math.max(left + width * (right ? 0 : 0.5), next.x),
        );
        if (clamped === next.x) continue;
        const requested = before + next.x - current.x;
        const destination = Math.max(0, Math.min(maximum, requested));
        container.scrollTo({ left: destination, behavior: 'smooth' });
        if (destination === requested && next.y === current.y) return;
        scrollCorrection = before - destination;
        break;
      }
    }
    this.props.onMove({
      x: next.x - this.reference.x + scrollCorrection,
      y: next.y - this.reference.y,
    });
  }

  private finish(reason: 'end' | 'cancel') {
    if (this.closed) return;
    this.resources.dispose();
    if (!this.props.options.scope.active) return;
    if (reason === 'end') this.props.onEnd();
    else this.props.onCancel();
  }
}
